<template>
  <v-container fluid class="teacher-page">
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />

    <Edit
      :editDialog="editDialog"
      :editTeacher="selectedTeacher"
      @close="editDialog = false"
      @submit="getList"
    />

    <AssignStudent
      :editDialog="assignStudentDialog"
      :editTeacher="selectedTeacher"
      @close="assignClose"
      @submit="getList"
    />

    <TeacherPasswordResetDialog
      :dialog="resetPasswordDialog"
      :teacher="selectedTeacher"
      @close="closePasswordReset"
    />

    <div class="teacher-page-heading">
      <div>
        <div class="page-eyebrow">Community</div>
        <h1>Teachers</h1>
        <p>Manage teacher profiles, portal access, login accounts, and assigned students.</p>
      </div>

      <v-btn
        v-if="checkRightStatus(21)"
        color="primary"
        depressed
        @click="add"
      >
        <v-icon left>mdi-plus</v-icon>
        New teacher
      </v-btn>
    </div>

    <v-row class="teacher-metrics">
      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-account-school-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ payees.length }}</div>
            <div class="metric-label">Total teachers</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-web</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ portalTeacherCount }}</div>
            <div class="metric-label">Portal access enabled</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-email-check-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ loginAccountCount }}</div>
            <div class="metric-label">Login accounts connected</div>
          </div>
        </div>
      </v-col>

      <v-col cols="6" sm="4" md="3">
        <div class="metric-item">
          <div class="metric-icon">
            <v-icon color="primary" size="21">mdi-account-group-outline</v-icon>
          </div>
          <div>
            <div class="metric-value">{{ totalAssignedStudents }}</div>
            <div class="metric-label">Assigned students</div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-card :loading="loading" class="teachers-card" elevation="0">
      <div class="teacher-filters">
        <v-text-field
          v-model="search"
          class="teacher-search"
          outlined
          prepend-inner-icon="mdi-magnify"
          dense
          background-color="#F8FAF8"
          placeholder="Search by teacher name or login email"
          single-line
          hide-details
          clearable
        />

        <v-btn
          icon
          outlined
          color="primary"
          :loading="loading"
          aria-label="Refresh teachers"
          @click="getList"
        >
          <v-icon size="19">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        class="teachers-table"
        :headers="headers"
        :items="payees"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      >
        <template v-slot:item="{ item }">
          <tr class="teacher-row">
            <td>
              <div class="teacher-identity">
                <div class="teacher-avatar">{{ teacherInitials(item.name) }}</div>
                <div>
                  <div class="teacher-name">{{ item.name }}</div>
                  <div class="teacher-subtitle">
                    {{ item.studentCount || 0 }}
                    {{ Number(item.studentCount || 0) === 1 ? "student" : "students" }}
                  </div>
                </div>
              </div>
            </td>

            <td>
              <span v-if="item.portalAccess" class="status-label">
                <v-icon color="primary" size="14">mdi-check-circle-outline</v-icon>
                Enabled
              </span>
              <span v-else class="status-label muted-status">
                <v-icon size="14">mdi-close-circle-outline</v-icon>
                Not enabled
              </span>
            </td>

            <td>
              <div v-if="item.LoginUser" class="login-email">
                <v-icon color="primary" size="16">mdi-email-outline</v-icon>
                <span>{{ item.LoginUser.email }}</span>
              </div>
              <div v-else class="login-email login-email-empty">
                <v-icon size="16">mdi-email-off-outline</v-icon>
                <span>Not set</span>
              </div>
            </td>

            <td class="text-center">
              <span class="student-count">{{ item.studentCount || 0 }}</span>
            </td>

            <td class="text-right">
              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    icon
                    aria-label="Teacher actions"
                  >
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>

                <v-list dense class="menu-button">
                  <v-list-item
                    v-if="checkRightStatus(23)"
                    @click="editTeacher(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-divider
                    v-if="checkRightStatus(23) && (canManagePortal(item) || checkRightStatus(24))"
                  />

                  <v-list-item
                    v-if="canManagePortal(item)"
                    @click="openPasswordReset(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-lock-reset</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Reset password</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="canManagePortal(item)"
                    @click="sendPasswordReset(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-email-fast-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Send reset link</v-list-item-title>
                  </v-list-item>

                  <v-divider
                    v-if="canManagePortal(item) && checkRightStatus(24)"
                  />

                  <v-list-item
                    v-if="checkRightStatus(24)"
                    @click="deleteTeacher(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="42" color="grey lighten-1">mdi-account-school-outline</v-icon>
            <strong>No teachers found</strong>
            <span>Add your first teacher to begin managing teacher access.</span>
            <v-btn
              v-if="checkRightStatus(21)"
              color="primary"
              depressed
              small
              @click="add"
            >
              <v-icon left small>mdi-plus</v-icon>
              Add teacher
            </v-btn>
          </div>
        </template>

        <template v-slot:no-results>
          <div class="empty-state">
            <v-icon size="42" color="grey lighten-1">mdi-magnify-close</v-icon>
            <strong>No matching teachers</strong>
            <span>No teachers matched "{{ search }}".</span>
            <v-btn text color="primary" small @click="search = null">
              Clear search
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-data-iterator
        v-else
        :items="payees"
        :search="search"
        :loading="loading"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :footer-props="{ 'items-per-page-options': [10, 25] }"
        class="mobile-teacher-list"
      >
        <template v-slot:default="{ items }">
          <div
            v-for="item in items"
            :key="item.id"
            class="mobile-teacher-card"
          >
            <div class="mobile-card-top">
              <div class="teacher-avatar">{{ teacherInitials(item.name) }}</div>

              <div class="mobile-card-identity">
                <strong>{{ item.name }}</strong>
                <span>
                  {{ item.studentCount || 0 }}
                  {{ Number(item.studentCount || 0) === 1 ? "student" : "students" }}
                </span>
              </div>

              <v-menu bottom left offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon aria-label="Teacher actions">
                    <v-icon>mdi-dots-horizontal</v-icon>
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item
                    v-if="checkRightStatus(23)"
                    @click="editTeacher(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-pencil-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="canManagePortal(item)"
                    @click="openPasswordReset(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-lock-reset</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Reset password</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="canManagePortal(item)"
                    @click="sendPasswordReset(item)"
                  >
                    <v-list-item-icon>
                      <v-icon size="19">mdi-email-fast-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>Send reset link</v-list-item-title>
                  </v-list-item>

                  <v-list-item
                    v-if="checkRightStatus(24)"
                    @click="deleteTeacher(item)"
                  >
                    <v-list-item-icon>
                      <v-icon color="error" size="19">mdi-delete-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <div class="mobile-teacher-details">
              <div>
                <span>Portal access</span>
                <strong :class="{ 'enabled-text': item.portalAccess }">
                  {{ item.portalAccess ? "Enabled" : "Not enabled" }}
                </strong>
              </div>

              <div>
                <span>Login email</span>
                <strong>{{ item.LoginUser ? item.LoginUser.email : "Not set" }}</strong>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon size="42" color="grey lighten-1">mdi-account-school-outline</v-icon>
            <strong>No teachers found</strong>
            <span>Add your first teacher to begin managing teacher access.</span>
          </div>
        </template>
      </v-data-iterator>
    </v-card>
  </v-container>
</template>

<script>
import teacherService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");
const AssignStudent = () => import("./AssignStudent");
const TeacherPasswordResetDialog = () => import("./TeacherPasswordResetDialog");

export default {
  name: "teacher-list",

  components: {
    Create,
    Edit,
    AssignStudent,
    TeacherPasswordResetDialog,
  },

  data() {
    return {
      createDialog: false,
      assignStudentDialog: false,
      editDialog: false,
      selectedTeacher: null,
      resetPasswordDialog: false,
      headers: [
        {
          text: "Teacher",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Portal access",
          align: "left",
          value: "portalAccess",
          sortable: true,
          width: "170px",
        },
        {
          text: "Login email",
          align: "left",
          value: "LoginUser.email",
          sortable: true,
        },
        {
          text: "Students",
          align: "center",
          value: "studentCount",
          sortable: true,
          width: "110px",
        },
        {
          text: "",
          align: "right",
          value: "action",
          sortable: false,
          width: "64px",
        },
      ],
      payees: [],
      search: null,
      loading: true,
      page: 1,
      sortBy: "name",
      itemsPerPage: 10,
    };
  },

  created() {
    this.getList();
    document.title = "taleem - Teacher List";
  },

  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    portalTeacherCount() {
      return this.payees.filter((teacher) => teacher.portalAccess).length;
    },

    loginAccountCount() {
      return this.payees.filter((teacher) => teacher.LoginUser).length;
    },

    totalAssignedStudents() {
      return this.payees.reduce((total, teacher) => {
        return total + Number(teacher.studentCount || 0);
      }, 0);
    },
  },

  methods: {
    teacherInitials(name) {
      return (name || "T")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },

    assignClose() {
      this.assignStudentDialog = false;
      this.getList();
    },

    canManagePortal(teacher) {
      return Boolean(
        this.checkRightStatus(23) &&
        teacher &&
        teacher.portalAccess &&
        teacher.LoginUser &&
        teacher.LoginUser.email
      );
    },

    openPasswordReset(teacher) {
      this.selectedTeacher = teacher;
      this.resetPasswordDialog = true;
    },

    closePasswordReset() {
      this.resetPasswordDialog = false;
      this.selectedTeacher = null;
    },

    async sendPasswordReset(teacher) {
      const confirmation = await this.$swal({
        title: "Send password reset link?",
        text: `A secure reset link will be emailed to ${teacher.LoginUser.email}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#43A047",
        confirmButtonText: "Send reset link",
      });
      if (!confirmation.value) return;
      try {
        await teacherService.sendPasswordReset(teacher.id);
        this.$swal({
          title: "Reset link sent",
          text: `The password reset email was sent to ${teacher.LoginUser.email}.`,
          icon: "success",
        });
      } catch (error) {
        const message = error && error.data && error.data.message
          ? error.data.message
          : "Unable to send the password reset email.";
        this.$swal({ title: "Reset link was not sent", text: message, icon: "error" });
      }
    },

    add() {
      this.createDialog = true;
    },

    getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      return teacherService
        .getAll()
        .then((response) => {
          this.payees = response.data || [];
          this.loading = false;
          return response;
        })
        .catch((error) => {
          this.loading = false;

          this.$swal({
            title: "Teacher List",
            text:
              error && error.data && error.data.message
                ? error.data.message
                : "Unable to load the teacher list.",
            icon: "error",
          });
        });
    },

    assignStudentOpen(obj) {
      this.selectedTeacher = obj;
      this.assignStudentDialog = true;
    },

    editTeacher(obj) {
      this.selectedTeacher = obj;
      this.editDialog = true;
    },

    deleteTeacher(obj) {
      this.$swal({
        title: "Are you sure?",
        text: `You want to delete ${obj.name}. If deleted, all ${
          obj.studentCount || 0
        } assigned students will also be removed.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          teacherService
            .delete(obj.id)
            .then((response) => {
              if (response.data) {
                this.$swal(
                  "Deleted!",
                  "Teacher has been deleted.",
                  "success"
                );
                this.getList();
              }
            })
            .catch((error) => {
              this.$swal({
                title: "Delete Teacher",
                text:
                  error && error.data && error.data.message
                    ? error.data.message
                    : "Unable to delete this teacher.",
                icon: "error",
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.teacher-page {
  min-height: 100%;
  padding: 30px 36px 48px;
  background: #f6f8f6;
}

.teacher-page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-eyebrow {
  margin-bottom: 5px;
  color: #43a047;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.teacher-page-heading h1 {
  margin: 0;
  color: #172033;
  font-size: 29px;
  line-height: 1.2;
  letter-spacing: -0.025em;
}

.teacher-page-heading p {
  margin: 6px 0 0;
  color: #728095;
  font-size: 13px;
}

.teacher-page-heading .v-btn {
  height: 42px;
  border-radius: 9px;
  text-transform: none;
  letter-spacing: 0;
}

.teacher-metrics {
  margin: 0 0 10px;
}

.metric-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.metric-icon {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eaf6eb;
}

.metric-value {
  color: #253047;
  font-size: 18px;
  font-weight: 750;
  line-height: 1.2;
}

.metric-label {
  margin-top: 2px;
  color: #7b8799;
  font-size: 11px;
}

.teachers-card {
  overflow: hidden;
  border: 1px solid #e3e9e3;
  border-radius: 11px !important;
}

.teacher-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 17px 20px;
  border-bottom: 1px solid #edf0ed;
}

.teacher-search {
  max-width: 470px;
}

.teacher-search ::v-deep .v-input__slot {
  border-radius: 9px !important;
}

.teachers-table ::v-deep th {
  height: 42px;
  color: #718075 !important;
  background: #f8faf8;
  font-size: 9px;
  font-weight: 750;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.teachers-table ::v-deep td {
  height: 66px;
  color: #425046;
  border-color: #edf0ed !important;
  font-size: 12px;
}

.teacher-row {
  transition: background 0.15s;
}

.teacher-row:hover {
  background: #f7fbf7 !important;
}

.teacher-identity {
  display: flex;
  align-items: center;
  gap: 11px;
}

.teacher-avatar {
  display: flex;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
  border-radius: 50%;
  background: #eaf6eb;
  font-size: 13px;
  font-weight: 750;
}

.teacher-name {
  color: #253047;
  font-size: 13px;
  font-weight: 650;
}

.teacher-subtitle {
  margin-top: 2px;
  color: #879288;
  font-size: 10px;
}

.status-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #2e7d32;
  font-size: 11px;
  font-weight: 650;
}

.muted-status {
  color: #8a968d;
}

.login-email {
  display: flex;
  align-items: center;
  gap: 7px;
  color: #445047;
  font-size: 12px;
}

.login-email-empty {
  color: #8a968d;
}

.student-count {
  display: inline-flex;
  min-width: 34px;
  height: 30px;
  padding: 0 9px;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
  border-radius: 8px;
  background: #eaf6eb;
  font-size: 16px;
  font-weight: 800;
}

.menu-button .v-list-item__icon {
  min-width: 30px;
  margin-right: 4px;
}

.empty-state {
  display: flex;
  min-height: 220px;
  padding: 40px 20px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  color: #526057;
  text-align: center;
}

.empty-state span {
  max-width: 340px;
  color: #929d95;
  font-size: 11px;
}

.empty-state .v-btn {
  margin-top: 8px;
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
}

.mobile-teacher-list {
  padding: 8px 12px 0;
}

.mobile-teacher-card {
  padding: 16px 3px;
  border-bottom: 1px solid #edf0ed;
}

.mobile-card-top {
  display: flex;
  align-items: center;
}

.mobile-card-identity {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  margin-left: 11px;
}

.mobile-card-identity strong {
  overflow: hidden;
  color: #253047;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-card-identity span {
  margin-top: 2px;
  color: #879288;
  font-size: 11px;
}

.mobile-teacher-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 14px 0 0 51px;
  padding-top: 11px;
  border-top: 1px solid #f0f2f0;
}

.mobile-teacher-details div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.mobile-teacher-details span {
  margin-bottom: 3px;
  color: #919b92;
  font-size: 10px;
  text-transform: uppercase;
}

.mobile-teacher-details strong {
  overflow: hidden;
  color: #445047;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.enabled-text {
  color: #2e7d32 !important;
}

@media (max-width: 599px) {
  .teacher-page {
    padding: 20px 14px 36px;
  }

  .teacher-page-heading {
    align-items: flex-start;
  }

  .teacher-page-heading h1 {
    font-size: 25px;
  }

  .teacher-page-heading p {
    max-width: 220px;
  }

  .teacher-page-heading .v-btn {
    min-width: 44px !important;
    padding: 0 11px !important;
    font-size: 0;
  }

  .teacher-page-heading .v-btn .v-icon {
    margin: 0 !important;
  }

  .teacher-metrics {
    margin-bottom: 8px;
  }

  .teacher-metrics > div {
    padding-top: 6px;
    padding-bottom: 12px;
  }

  .metric-value {
    font-size: 15px;
  }

  .teacher-filters {
    gap: 12px;
    padding: 14px;
  }

  .teacher-search {
    max-width: none;
    flex: 1;
  }
}
</style>
